/**
 * CustomerDocumentReviewPage — Issue #174 + Issue #345.
 *
 * Admin queue for reviewing, publishing, retracting customer-uploaded documents.
 *
 * Issue #345 wraps the previous hand-coded table with `AdminDataTable` so the
 * page gains shared advanced-search, column-visibility, loading/empty/error
 * states and a permission-aware row action menu. The mutation contract is
 * unchanged — every action still calls the real backend and re-reads the list
 * from the server response. Customer-document records are PHI-adjacent so
 * physical deletion is intentionally NOT exposed; the only destructive
 * operation is `retract`, which the backend implements as a logical state
 * transition (`published` → `retracted`).
 *
 * Red lines (unchanged from #174):
 *   - Only the `useCustomerDocuments` hook talks to the backend.
 *   - 拒绝/撤回 must enforce non-empty reasons (UI + backend).
 *   - Downloads go through transient signed URLs only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Download,
  Send,
  Undo2,
  XCircle,
} from 'lucide-react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  CUSTOMER_DOCUMENT_PERMISSIONS,
  hasPermission,
} from '@/lib/customerDocumentPermissions';
import {
  useCustomerDocuments,
  type CustomerDocumentAdminDto,
  type CustomerDocumentReviewState,
} from '@/hooks/useCustomerDocuments';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';
import { Button } from '@/components/ui/button';

type DocKind = CustomerDocumentAdminDto['docKind'];

const REVIEW_STATES: readonly CustomerDocumentReviewState[] = [
  'awaiting_upload',
  'pending_review',
  'approved',
  'rejected',
  'published',
  'retracted',
];

const DOC_KIND_LABELS: Record<DocKind, string> = {
  health_report: '体检报告',
  medical_image: '医学影像',
  photo: '照片',
  video: '视频',
  document: '文档',
  identity: '身份资料',
  other: '其他',
};

const STATE_LABELS: Record<CustomerDocumentReviewState, string> = {
  awaiting_upload: '待上传',
  pending_review: '待审核',
  approved: '已通过',
  rejected: '已驳回',
  published: '已发布',
  retracted: '已撤回',
};

const STATE_TONE: Record<CustomerDocumentReviewState, string> = {
  awaiting_upload: 'bg-slate-100 text-slate-600',
  pending_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  published: 'bg-blue-100 text-blue-700',
  retracted: 'bg-purple-100 text-purple-700',
};

const BADGE_BASE = 'inline-flex rounded-full px-2 py-1 text-xs font-medium';

function fmtDateTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

function fmtBytes(value?: string | null): string {
  if (!value) return '—';
  const bytes = BigInt(value);
  const ONE_KB = 1024n;
  const ONE_MB = 1024n * 1024n;
  if (bytes < ONE_MB) {
    const kb = bytes / ONE_KB;
    return `${kb.toString()} KB`;
  }
  const mb = bytes / ONE_MB;
  const remainder = (bytes % ONE_MB) * 10n / ONE_MB;
  return `${mb.toString()}.${remainder.toString()} MB`;
}

interface RejectModalState {
  documentId: string;
  expectedVersion: number;
  reason: string;
}

interface RetractModalState {
  documentId: string;
  expectedVersion: number;
  reason: string;
}

export default function CustomerDocumentReviewPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    CUSTOMER_DOCUMENT_PERMISSIONS.READ,
  );
  const canReview = hasPermission(
    permissions,
    CUSTOMER_DOCUMENT_PERMISSIONS.REVIEW,
  );
  const canPublish = hasPermission(
    permissions,
    CUSTOMER_DOCUMENT_PERMISSIONS.PUBLISH,
  );

  const {
    documents,
    loading,
    error,
    fetchDocuments,
    approveDocument,
    rejectDocument,
    publishDocument,
    retractDocument,
    fetchDownloadGrant,
  } = useCustomerDocuments();

  // Issue #345 audit fix2: 后端 `/api/v1/customer-documents` 仅按 reviewState /
  // docKind / customerId / serviceUserId 过滤，title 不在查询契约里。先前的
  // `title` 文本筛选只会在前端命中已加载数组，会与（未来若加分页时）的真实命
  // 中数和翻页 total 不一致；在后端补齐 title 模糊查询前，工具栏只保留 state
  // 下拉这个服务端真正支持的筛选项。
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    state: 'pending_review',
  });
  const [reject, setReject] = useState<RejectModalState | null>(null);
  const [retract, setRetract] = useState<RetractModalState | null>(null);

  const stateFilter = (searchValues['state'] ?? '') as
    | CustomerDocumentReviewState
    | '';

  useEffect(() => {
    if (!canRead) return;
    void fetchDocuments(stateFilter ? { reviewState: stateFilter } : undefined);
  }, [canRead, stateFilter, fetchDocuments]);

  const refresh = useCallback((): void => {
    void fetchDocuments(stateFilter ? { reviewState: stateFilter } : undefined);
  }, [fetchDocuments, stateFilter]);

  const onApprove = useCallback(
    async (doc: CustomerDocumentAdminDto) => {
      if (!canReview) return;
      const next = await approveDocument(doc.id, {
        expectedVersion: doc.version,
      });
      if (next) refresh();
    },
    [approveDocument, canReview, refresh],
  );

  const onPublish = useCallback(
    async (doc: CustomerDocumentAdminDto) => {
      if (!canPublish) return;
      const next = await publishDocument(doc.id, {
        expectedVersion: doc.version,
      });
      if (next) refresh();
    },
    [canPublish, publishDocument, refresh],
  );

  const onDownload = useCallback(
    async (doc: CustomerDocumentAdminDto) => {
      const grant = await fetchDownloadGrant(doc.id);
      if (grant) {
        const anchor = document.createElement('a');
        anchor.href = grant.downloadUrl;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.click();
      }
    },
    [fetchDownloadGrant],
  );

  const visibleRows = useMemo(
    () =>
      [...documents].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [documents],
  );

  const columns: ReadonlyArray<AdminDataTableColumn<CustomerDocumentAdminDto>> =
    useMemo(
      () => [
        {
          key: 'title',
          header: '标题',
          alwaysVisible: true,
          render: (doc) => (
            <div className="space-y-1">
              <div className="font-medium text-slate-800">{doc.title}</div>
              {doc.description ? (
                <div className="text-xs text-slate-500">{doc.description}</div>
              ) : null}
              {doc.rejectReason ? (
                <div className="text-xs text-red-600">
                  驳回原因：{doc.rejectReason}
                </div>
              ) : null}
              {doc.retractReason ? (
                <div className="text-xs text-purple-600">
                  撤回原因：{doc.retractReason}
                </div>
              ) : null}
            </div>
          ),
        },
        {
          key: 'docKind',
          header: '类型',
          render: (doc) => DOC_KIND_LABELS[doc.docKind],
        },
        {
          key: 'reviewState',
          header: '状态',
          render: (doc) => (
            <span className={cn(BADGE_BASE, STATE_TONE[doc.reviewState])}>
              {STATE_LABELS[doc.reviewState]}
            </span>
          ),
        },
        {
          key: 'customerId',
          header: '客户',
          render: (doc) => (
            <span className="font-mono text-xs">{doc.customerId}</span>
          ),
        },
        {
          key: 'serviceUserId',
          header: '服务用户',
          defaultHidden: true,
          render: (doc) => (
            <span className="font-mono text-xs">
              {doc.serviceUserId ?? '—'}
            </span>
          ),
        },
        {
          key: 'declaredByteSize',
          header: '大小',
          defaultHidden: true,
          render: (doc) => fmtBytes(doc.declaredByteSize),
        },
        {
          key: 'finalizedAt',
          header: '上传时间',
          render: (doc) => (
            <span className="text-xs text-slate-500">
              {fmtDateTime(doc.finalizedAt ?? doc.createdAt)}
            </span>
          ),
        },
        {
          key: 'version',
          header: '版本',
          defaultHidden: true,
          render: (doc) => <span className="text-xs">v{doc.version}</span>,
        },
      ],
      [],
    );

  const rowActions: ReadonlyArray<
    AdminDataTableAction<CustomerDocumentAdminDto>
  > = useMemo(
    () => [
      {
        key: 'download',
        label: '查看',
        icon: <Download className="h-3.5 w-3.5" />,
        visible: (doc) => doc.reviewState !== 'awaiting_upload',
        onSelect: (doc) => void onDownload(doc),
      },
      {
        key: 'approve',
        label: '通过',
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        visible: (doc) => canReview && doc.reviewState === 'pending_review',
        onSelect: (doc) => void onApprove(doc),
      },
      {
        key: 'reject',
        label: '驳回',
        icon: <XCircle className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (doc) => canReview && doc.reviewState === 'pending_review',
        onSelect: (doc) =>
          setReject({
            documentId: doc.id,
            expectedVersion: doc.version,
            reason: '',
          }),
      },
      {
        key: 'publish',
        label: '发布给客户',
        icon: <Send className="h-3.5 w-3.5" />,
        visible: (doc) =>
          canPublish &&
          (doc.reviewState === 'approved' || doc.reviewState === 'retracted'),
        onSelect: (doc) => void onPublish(doc),
      },
      {
        key: 'retract',
        label: '撤回（归档）',
        icon: <Undo2 className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (doc) => canPublish && doc.reviewState === 'published',
        onSelect: (doc) =>
          setRetract({
            documentId: doc.id,
            expectedVersion: doc.version,
            reason: '',
          }),
      },
    ],
    [canPublish, canReview, onApprove, onDownload, onPublish],
  );

  if (!canRead) {
    return <AccessDenied resource="客户资料审核" />;
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">客户资料审核</h1>
          <p className="text-sm text-slate-500">
            审核客户提交的健康资料、影像、照片等，并控制对客户的可见性。客户资料属于
            高敏感数据，仅支持作废 / 归档（撤回），不提供物理删除。
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </header>

      <AdminDataTable<CustomerDocumentAdminDto>
        testIdPrefix="customer-documents"
        columns={columns}
        rows={visibleRows}
        isLoading={loading && documents.length === 0}
        error={error}
        onRetry={refresh}
        emptyMessage="暂无资料"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'state',
              label: '状态',
              options: REVIEW_STATES.map((s) => ({
                value: s,
                label: STATE_LABELS[s],
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => setSearchValues(next),
          onReset: () => setSearchValues({ state: '' }),
        }}
        rowActions={rowActions}
      />

      {reject ? (
        <ReasonModal
          key={`reject-${reject.documentId}`}
          title="驳回客户资料"
          confirmLabel="确认驳回"
          confirmTone="bg-red-600 hover:bg-red-700"
          value={reject.reason}
          onChange={(v) => setReject({ ...reject, reason: v })}
          onCancel={() => setReject(null)}
          onConfirm={async () => {
            const trimmed = reject.reason.trim();
            if (trimmed.length === 0) return;
            const next = await rejectDocument(reject.documentId, {
              expectedVersion: reject.expectedVersion,
              reason: trimmed,
            });
            if (next) {
              setReject(null);
              refresh();
            }
          }}
        />
      ) : null}

      {retract ? (
        <ReasonModal
          key={`retract-${retract.documentId}`}
          title="撤回客户可见资料"
          confirmLabel="确认撤回"
          confirmTone="bg-purple-600 hover:bg-purple-700"
          value={retract.reason}
          onChange={(v) => setRetract({ ...retract, reason: v })}
          onCancel={() => setRetract(null)}
          onConfirm={async () => {
            const trimmed = retract.reason.trim();
            if (trimmed.length === 0) return;
            const next = await retractDocument(retract.documentId, {
              expectedVersion: retract.expectedVersion,
              reason: trimmed,
            });
            if (next) {
              setRetract(null);
              refresh();
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface ReasonModalProps {
  title: string;
  confirmLabel: string;
  confirmTone: string;
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

function ReasonModal({
  title,
  confirmLabel,
  confirmTone,
  value,
  onChange,
  onCancel,
  onConfirm,
}: ReasonModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">
          请说明原因。该原因将写入审计日志；驳回原因会向客户展示。
        </p>
        <textarea
          className="mt-3 h-28 w-full rounded border border-slate-300 px-2 py-1 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={2000}
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 text-sm"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className={cn(
              'rounded px-3 py-1 text-sm text-white',
              confirmTone,
              value.trim().length === 0 && 'opacity-50',
            )}
            disabled={value.trim().length === 0}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
